import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode, type RefObject } from "react"
import { createPortal } from "react-dom"
import {
  Aperture,
  Circle,
  Drone,
  Gauge,
  Grid2x2X,
  HelpCircle,
  History,
  Loader2,
  LocateFixed,
  MapPin,
  Minus,
  MonitorSmartphone,
  Plus,
  Redo2,
  Scaling,
  SolarPanel,
  Undo2,
  X,
  type LucideIcon,
} from "lucide-react"
import { CameraPlaceIcon } from "@/components/icons/camera-place-icon"
import { CameraTrajectoryIcon } from "@/components/icons/camera-trajectory-icon"
import { ActionHintTooltip } from "@/components/ui/action-hint-tooltip"
import { LocationScene3dControlsHint } from "@/components/locations/location-scene-3d-controls-hint"
import { MENU_Z_INDEX, useDropdownMenu } from "@/components/ui/dropdown-menu-position"
import { EditToolbarButton } from "@/components/ui/edit-toolbar-button"
import { EditToolbarMore, type EditToolbarMoreItem } from "@/components/ui/edit-toolbar-more"
import { Button, Input } from "@/components/ui/primitives"
import { UiRange } from "@/components/ui/ui-range"
import { FovControl } from "@/components/locations/fov-control"
import { PlatformSectorCountInput } from "@/components/locations/platform-sector-count-input"
import { Scene3dFlyQr } from "@/components/locations/scene-3d-fly-qr"
import { useTranslation } from "@/hooks/use-translation"
import { SCENE_3D_TOOLBAR_MENU_ATTR } from "@/lib/dialog-layer"
import { fovMenuSize } from "@/lib/location-scene-3d/fov-control-math"
import type { HistoryEntry } from "@/lib/location-scene-3d/action-history"
import type { PlatformUnit, ScenePlatform } from "@/lib/location-scene-3d/platform"
import { sectorsFromDraft } from "@/lib/location-scene-3d/platform-size-draft"
import type { SceneSkyMarkup } from "@/lib/location-scene-3d/scene-state"
import type { PlatformLightSide } from "@/lib/location-scene-3d/platform-layer"
import { cn } from "@/lib/utils"
import "@/components/locations/scene-controls.css"
import {
  computeSceneToolbarFit,
  FALLBACK_SCENE_TOOLBAR_WIDTHS,
  sceneOverflowSetsEqual,
  type SceneOverflowKey,
  type SceneToolbarMeasuredWidths,
} from "@/components/locations/location-scene-3d-toolbar-fit"

/** Same height as agent-column-header: py-2.5 + text-xs line (16px) = 36px; h-8 buttons + py-0.5. */
export const TOOLBAR_ROW =
  "flex w-full min-w-0 shrink-0 flex-nowrap items-center gap-1.5 overflow-hidden border-b border-ui-border bg-ui-bg-elevated px-2 py-0.5"

/** Имя не отдаёт ширину кнопкам, пока ряд не уже этого запаса. На телефоне меньше — рядом кнопка пролёта. */
const SCENE_NAME_MIN_PX = 200
const SCENE_NAME_MIN_PX_PHONE = 120
/** TOOLBAR_ROW px-2. */
const TOOLBAR_PAD_X_PX = 16

const DIVIDER = <span className="mx-0.5 h-5 w-px shrink-0 bg-ui-border" aria-hidden />

const SCENE3D_NAME_MAX_LENGTH = 200

function SceneToolbarName({
  name,
  canEdit,
  onCommit,
}: {
  name: string
  canEdit: boolean
  onCommit: (name: string) => void
}) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(name)
  const inputRef = useRef<HTMLInputElement>(null)
  const skipCommitRef = useRef(false)
  const committedRef = useRef(false)

  useEffect(() => {
    if (!editing) setDraft(name)
  }, [editing, name])

  useEffect(() => {
    if (!editing) return
    committedRef.current = false
    inputRef.current?.select()
  }, [editing])

  const commit = () => {
    if (skipCommitRef.current) {
      skipCommitRef.current = false
      return
    }
    if (committedRef.current) return
    committedRef.current = true
    setEditing(false)
    const next = (inputRef.current?.value ?? draft).trim()
    if (!next || next === name) return
    onCommit(next)
  }

  const cancel = () => {
    skipCommitRef.current = true
    setDraft(name)
    setEditing(false)
  }

  if (!canEdit) {
    return (
      <span className="block w-full min-w-0 truncate text-left text-xs font-medium text-ui-text-primary">{name}</span>
    )
  }

  if (!editing) {
    return (
      <button
        type="button"
        className="block w-full min-w-0 truncate rounded-lg px-2 py-1 text-left text-xs font-medium text-ui-text-primary"
        onClick={() => {
          setDraft(name)
          setEditing(true)
        }}
      >
        {name}
      </button>
    )
  }

  return (
    <Input
      ref={inputRef}
      value={draft}
      maxLength={SCENE3D_NAME_MAX_LENGTH}
      aria-label={name}
      className="h-8 w-full min-w-0 px-2 text-xs"
      onChange={(event) => setDraft(event.target.value)}
      onBlur={commit}
      onKeyDown={(event) => {
        if (event.key === "Enter") {
          event.preventDefault()
          commit()
        }
        if (event.key === "Escape") {
          event.preventDefault()
          cancel()
        }
      }}
    />
  )
}

/** Кнопка траектории и пилюля под ней. Пилюля не закрывается кликом снаружи. */
function TrajectoryTool({
  active,
  title,
  panel,
  onToggle,
}: {
  active: boolean
  title: string
  panel?: ReactNode
  onToggle: () => void
}) {
  const rootRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const [panelSize, setPanelSize] = useState(0)
  const open = active && panel != null

  const { menuStyle, portalRoot } = useDropdownMenu({
    open,
    direction: "down",
    align: "center",
    anchorRef: buttonRef,
    menuRef,
    rootRef,
    resolveWidth: () => ({ width: "max-content" }),
    onOutsideClick: () => {},
    positionKey: panelSize,
    estimatedMenuHeightPx: 44,
    estimatedMenuWidthPx: 480,
    // Тот же слой, что у соседних меню тулбара: внутри диалога сцены, не поверх просмотра.
    portalToBody: false,
    zIndex: MENU_Z_INDEX,
  })

  useEffect(() => {
    const el = menuRef.current
    if (!open || !el) return
    let width = el.offsetWidth
    let height = el.offsetHeight
    const observer = new ResizeObserver(() => {
      if (el.offsetWidth === width && el.offsetHeight === height) return
      width = el.offsetWidth
      height = el.offsetHeight
      setPanelSize(width + height)
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [open, portalRoot])

  return (
    <div ref={rootRef} className="relative inline-flex shrink-0 leading-none">
      <EditToolbarButton ref={buttonRef} title={title} hint="tooltip" active={active} onClick={onToggle}>
        <CameraTrajectoryIcon className="h-4 w-4" />
      </EditToolbarButton>
      {open && menuStyle && portalRoot
        ? createPortal(
            <div
              ref={menuRef}
              style={menuStyle}
              className="[&_button]:focus-visible:ring-0"
              {...{ [SCENE_3D_TOOLBAR_MENU_ATTR]: "" }}
            >
              {panel}
            </div>,
            portalRoot,
          )
        : null}
    </div>
  )
}

/** Icon button + portaled panel — escapes TOOLBAR_ROW overflow-hidden. */
function SceneToolbarMenu({
  open,
  onOpenChange,
  title,
  icon,
  onBeforeOpen,
  disabled,
  align = "left",
  estimatedMenuWidthPx = 224,
  estimatedMenuHeightPx = 240,
  maxWidth = "min(24rem, calc(100vw - 16px))",
  ignoreOutside,
  panelClassName,
  children,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  icon: ReactNode
  /** Return false to cancel open. May be async (e.g. QR draft bind). */
  onBeforeOpen?: () => void | boolean | Promise<void | boolean>
  disabled?: boolean
  align?: "left" | "right"
  estimatedMenuWidthPx?: number
  estimatedMenuHeightPx?: number
  maxWidth?: string
  /** Clicks on the 3D canvas keep a tool menu open. */
  ignoreOutside?: (target: EventTarget | null) => boolean
  panelClassName?: string
  children: ReactNode
}) {
  const rootRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  const { menuStyle, portalRoot } = useDropdownMenu({
    open,
    direction: "down",
    align,
    anchorRef: buttonRef,
    menuRef,
    rootRef,
    resolveWidth: () => ({
      width: "max-content",
      maxWidth,
    }),
    onOutsideClick: (event) => {
      if (ignoreOutside?.(event.target)) return
      onOpenChange(false)
    },
    estimatedMenuHeightPx,
    estimatedMenuWidthPx,
    portalToBody: true,
  })

  useEffect(() => {
    if (!open) return
    const handleKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === "Escape") onOpenChange(false)
    }
    document.addEventListener("keydown", handleKey, true)
    return () => document.removeEventListener("keydown", handleKey, true)
  }, [open, onOpenChange])

  const panel =
    open && menuStyle ? (
      <div
        ref={menuRef}
        style={menuStyle}
        className={cn(
          "rounded-lg border border-ui-border bg-ui-bg-elevated shadow-[var(--ui-shadow-md)] [&_button]:focus-visible:ring-0",
          panelClassName,
        )}
        {...{ [SCENE_3D_TOOLBAR_MENU_ATTR]: "" }}
      >
        {children}
      </div>
    ) : null

  return (
    <div ref={rootRef} className="relative inline-flex shrink-0 leading-none">
      <EditToolbarButton
        ref={buttonRef}
        title={title}
        hint="tooltip"
        active={open}
        disabled={disabled}
        onClick={() => {
          void (async () => {
            if (open) {
              onOpenChange(false)
              return
            }
            if (onBeforeOpen) {
              const allowed = await onBeforeOpen()
              if (allowed === false) return
            }
            onOpenChange(true)
          })()
        }}
      >
        {icon}
      </EditToolbarButton>
      {panel && portalRoot ? createPortal(panel, portalRoot) : null}
    </div>
  )
}

const FOV_MENU = fovMenuSize(100)
const FOV_MENU_WIDTH_PX = FOV_MENU.width
const FOV_MENU_HEIGHT_PX = FOV_MENU.height

function SceneFovMenuControl({ value, onValueChange }: { value: number; onValueChange: (value: number) => void }) {
  return <FovControl value={value} min={25} max={100} onInput={onValueChange} onChange={onValueChange} />
}

type ToolbarSliderButtonProps = {
  icon: LucideIcon
  label: string
  valueText?: string
  min: number
  max: number
  step?: number
  value: number
  onValueChange: (v: number) => void
  onOpen?: () => void
  disabled?: boolean
  /** Dark chip over the 3D stage (legacy). */
  stageChrome?: boolean
  /** Icon on the button; "Label: N" sits above the slider. */
  valueInPanel?: boolean
  /** Replaces the horizontal range. FOV passes the fan. */
  control?: ReactNode
  menuWidthPx?: number
  menuHeightPx?: number
  /** Horizontal attachment of the menu to the button. FOV uses center. */
  menuAlign?: "left" | "right" | "center"
}

/** Fly-speed (or similar) slider; portals menu above body-level stage. */
export function ToolbarSliderButton({
  icon: Icon,
  label,
  valueText,
  min,
  max,
  step = 1,
  value,
  onValueChange,
  onOpen,
  disabled,
  stageChrome = false,
  valueInPanel = false,
  control,
  menuWidthPx,
  menuHeightPx,
  menuAlign = "left",
}: ToolbarSliderButtonProps) {
  const rootRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(false)

  const { menuStyle, portalRoot } = useDropdownMenu({
    open,
    direction: "down",
    align: menuAlign,
    anchorRef: buttonRef,
    menuRef,
    rootRef,
    resolveWidth: () => ({ width: menuWidthPx != null ? `${menuWidthPx}px` : "13rem" }),
    onOutsideClick: () => setOpen(false),
    estimatedMenuHeightPx: menuHeightPx ?? (valueInPanel ? 72 : 44),
    estimatedMenuWidthPx: menuWidthPx ?? 208,
    portalToBody: true,
  })

  useEffect(() => {
    if (!open) return
    const handleKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false)
    }
    document.addEventListener("keydown", handleKey, true)
    return () => document.removeEventListener("keydown", handleKey, true)
  }, [open])

  const panel =
    open && menuStyle ? (
      <div
        ref={menuRef}
        style={menuStyle}
        className={cn(
          "rounded-lg border border-ui-border bg-ui-bg-elevated shadow-[var(--ui-shadow-md)]",
          control ? "p-2" : "px-3 py-2",
          !control && valueInPanel && "flex flex-col gap-2",
          !control && !valueInPanel && "flex items-center gap-2",
        )}
        {...{ [SCENE_3D_TOOLBAR_MENU_ATTR]: "" }}
      >
        {control ?? (
          <>
            {valueInPanel ? <p className="text-[11px] text-ui-text-primary">{`${label}: ${value}`}</p> : null}
            <UiRange
              min={min}
              max={max}
              step={step}
              value={value}
              onChange={(e) => onValueChange(Number(e.target.value))}
              className="min-w-0 flex-1"
              aria-label={label}
            />
            {valueInPanel ? null : (
              <span className="w-8 shrink-0 text-right font-mono text-[11px] text-ui-text-muted">{value}</span>
            )}
          </>
        )}
      </div>
    ) : null

  return (
    <div ref={rootRef} className={cn("inline-flex shrink-0 leading-none", stageChrome && "rounded-lg bg-black/55")}>
      <ActionHintTooltip label={label}>
        <Button
          ref={buttonRef}
          type="button"
          size="sm"
          variant="ghost"
          disabled={disabled}
          aria-label={label}
          aria-expanded={open}
          className={cn(
            "h-8 shrink-0 gap-1 px-1.5",
            stageChrome
              ? cn(
                  "rounded-lg border-0 bg-transparent text-white/90 hover:bg-white/20 hover:text-white",
                  open && "bg-white/20 text-white hover:bg-white/20",
                )
              : open && "bg-ui-accent-soft text-ui-accent-text",
          )}
          onClick={() =>
            setOpen((prev) => {
              const next = !prev
              if (next) onOpen?.()
              return next
            })
          }
        >
          <Icon className="h-3.5 w-3.5" />
          {valueText ? <span className="font-mono text-[11px] tabular-nums">{valueText}</span> : null}
        </Button>
      </ActionHintTooltip>
      {panel && portalRoot ? createPortal(panel, portalRoot) : null}
    </div>
  )
}

export type LocationScene3dToolbarProps = {
  canEdit: boolean
  showSaveStart: boolean
  showGoToStart: boolean
  goToStartBusy: boolean
  canGoToStart: boolean
  onSaveStart: () => void
  onGoToStart: () => void
  canUndo: boolean
  onUndo: () => void
  canRedo: boolean
  onRedo: () => void
  historyEntries: HistoryEntry[]
  historyOpen: boolean
  onHistoryOpenChange: (open: boolean) => void
  onClearHistory: () => void
  onRestoreHistory: (entry: HistoryEntry) => void
  lightPreset: string
  lightMenuOpen: boolean
  onLightMenuOpenChange: (open: boolean) => void
  onApplyLightPreset: (side: PlatformLightSide) => void
  platformDraft: ScenePlatform | null
  onPlatformDraftChange: (patch: Partial<ScenePlatform>) => void
  onPlatformApply: (patch?: Partial<ScenePlatform>) => void
  platformBusy: boolean
  flySpeed: number
  onFlySpeedChange: (v: number) => void
  fov: number
  onFovChange: (v: number) => void
  showFlyQr: boolean
  flyUrl: string
  flyQrLabel: string
  /** Ensure draft before opening QR; return false to cancel. */
  onFlyQrBeforeOpen?: () => boolean | Promise<boolean>
  flyQrBusy?: boolean
  showViewQr: boolean
  viewUrl: string
  viewQrLabel: string
  /** Phone / coarse pointer layout (DEVICE_CAMERA_MQ) — close, no hotkey help. */
  phoneLayout?: boolean
  /** Phone only: open the fly modal. Not the QR menu. */
  onEnterPhoneFly?: () => void
  onRequestClose?: () => void
  trajectoryActive?: boolean
  onToggleTrajectory?: () => void
  /** Пилюля режима траектории. Портал под кнопку, пока режим включён. */
  trajectoryPanel?: ReactNode
  /** Установка камеры. На телефоне кнопки нет. */
  showCameraPlace?: boolean
  cameraPlaceActive?: boolean
  cameraPlaceDisabled?: boolean
  /** Принудительная подсказка на кнопку в ряду. Пока она есть, ховер-тултип не дублируется. */
  cameraPlaceHint?: string
  onToggleCameraPlace?: () => void
  /** Другая кнопка тулбара: выйти из позиционирования. Не отмена, повтор, история и сама кнопка камеры. */
  onLeaveCameraPlace?: () => void
  addButtonRef?: RefObject<HTMLButtonElement | null>
  pickerOpen?: boolean
  /** Plus stays lit only when this picker was opened from the toolbar. */
  pickerToolbarActive?: boolean
  onTogglePicker?: () => void
  /** Close the picker when another toolbar menu opens. Does not move its anchor. */
  onDismissPicker?: () => void
  skyMarkup: SceneSkyMarkup
  onSkyMarkupChange: (markup: SceneSkyMarkup) => void
  sceneName: string
  onSceneNameCommit: (name: string) => void
}

function PlatformDimsControls({
  draft,
  canEdit,
  busy,
  onChange,
  onApply,
}: {
  draft: ScenePlatform
  canEdit: boolean
  busy: boolean
  onChange: (patch: Partial<ScenePlatform>) => void
  onApply: (patch?: Partial<ScenePlatform>) => void
}) {
  const { t } = useTranslation(["workspace"])
  const [xDraft, setXDraft] = useState(() => String(draft.sectors_x))
  const [zDraft, setZDraft] = useState(() => String(draft.sectors_z))
  const xFocused = useRef(false)
  const zFocused = useRef(false)

  useEffect(() => {
    if (xFocused.current) return
    setXDraft(String(draft.sectors_x))
  }, [draft.sectors_x])

  useEffect(() => {
    if (zFocused.current) return
    setZDraft(String(draft.sectors_z))
  }, [draft.sectors_z])

  const commitX = () => {
    xFocused.current = false
    const next = sectorsFromDraft(xDraft)
    setXDraft(String(next))
    if (next !== draft.sectors_x) onChange({ sectors_x: next })
  }

  const commitZ = () => {
    zFocused.current = false
    const next = sectorsFromDraft(zDraft)
    setZDraft(String(next))
    if (next !== draft.sectors_z) onChange({ sectors_z: next })
  }

  const apply = () => {
    const sectorsX = sectorsFromDraft(xDraft)
    const sectorsZ = sectorsFromDraft(zDraft)
    xFocused.current = false
    zFocused.current = false
    setXDraft(String(sectorsX))
    setZDraft(String(sectorsZ))
    onApply({ sectors_x: sectorsX, sectors_z: sectorsZ })
  }

  return (
    <div className="flex w-40 flex-col gap-2">
      <label className="flex flex-col gap-0.5">
        <span className="text-[10px] text-ui-text-muted">{t("locationsPage.scenePlatform.sectorsX")}</span>
        <PlatformSectorCountInput
          value={xDraft}
          disabled={!canEdit}
          title={t("locationsPage.scenePlatform.sectorsX")}
          aria-label={t("locationsPage.scenePlatform.sectorsX")}
          onFocus={() => {
            xFocused.current = true
          }}
          onValueChange={setXDraft}
          onCommit={commitX}
          className="h-8 px-1.5 text-xs"
        />
      </label>
      <label className="flex flex-col gap-0.5">
        <span className="text-[10px] text-ui-text-muted">{t("locationsPage.scenePlatform.sectorsZ")}</span>
        <PlatformSectorCountInput
          value={zDraft}
          disabled={!canEdit}
          title={t("locationsPage.scenePlatform.sectorsZ")}
          aria-label={t("locationsPage.scenePlatform.sectorsZ")}
          onFocus={() => {
            zFocused.current = true
          }}
          onValueChange={setZDraft}
          onCommit={commitZ}
          className="h-8 px-1.5 text-xs"
        />
      </label>
      <label className="flex flex-col gap-0.5">
        <span className="text-[10px] text-ui-text-muted">{t("locationsPage.scenePlatform.unit")}</span>
        <select
          className="h-8 rounded-lg border border-ui-border bg-ui-surface-sunken px-1.5 text-xs text-ui-text-primary"
          disabled={!canEdit}
          value={draft.unit}
          onChange={(event) => onChange({ unit: event.target.value as PlatformUnit })}
        >
          <option value="cm">{t("locationsPage.scenePlatform.unitCm")}</option>
          <option value="m">{t("locationsPage.scenePlatform.unitM")}</option>
        </select>
      </label>
      {canEdit ? (
        <Button type="button" size="sm" className="h-8 w-full text-xs" disabled={busy} onClick={apply}>
          {t("locationsPage.scenePlatform.apply")}
        </Button>
      ) : null}
    </div>
  )
}

function measuredOuterWidth(el: Element | null, fallback: number): number {
  if (!(el instanceof HTMLElement)) return fallback
  const rect = el.getBoundingClientRect()
  const style = window.getComputedStyle(el)
  return rect.width + (Number.parseFloat(style.marginLeft) || 0) + (Number.parseFloat(style.marginRight) || 0)
}

export function LocationScene3dToolbar({
  canEdit,
  showSaveStart,
  showGoToStart,
  goToStartBusy,
  canGoToStart,
  onSaveStart,
  onGoToStart,
  canUndo,
  onUndo,
  canRedo,
  onRedo,
  historyEntries,
  historyOpen,
  onHistoryOpenChange,
  onClearHistory,
  onRestoreHistory,
  lightPreset,
  lightMenuOpen,
  onLightMenuOpenChange,
  onApplyLightPreset,
  platformDraft,
  onPlatformDraftChange,
  onPlatformApply,
  platformBusy,
  flySpeed,
  onFlySpeedChange,
  fov,
  onFovChange,
  showFlyQr,
  flyUrl,
  flyQrLabel,
  onFlyQrBeforeOpen,
  flyQrBusy = false,
  showViewQr,
  viewUrl,
  viewQrLabel,
  phoneLayout = false,
  onEnterPhoneFly,
  onRequestClose,
  trajectoryActive = false,
  onToggleTrajectory,
  trajectoryPanel,
  showCameraPlace = false,
  cameraPlaceActive = false,
  cameraPlaceDisabled = false,
  cameraPlaceHint,
  onToggleCameraPlace,
  onLeaveCameraPlace,
  addButtonRef,
  pickerOpen = false,
  pickerToolbarActive,
  onTogglePicker,
  onDismissPicker,
  skyMarkup,
  onSkyMarkupChange,
  sceneName,
  onSceneNameCommit,
}: LocationScene3dToolbarProps) {
  const { t } = useTranslation(["workspace"])
  const toolbarRootRef = useRef<HTMLDivElement>(null)
  const measureRef = useRef<HTMLDivElement>(null)
  const moreButtonRef = useRef<HTMLButtonElement>(null)
  const [moreOpen, setMoreOpen] = useState(false)
  const [platformMenuOpen, setPlatformMenuOpen] = useState(false)
  const [qrMenuOpen, setQrMenuOpen] = useState(false)
  const [viewQrMenuOpen, setViewQrMenuOpen] = useState(false)
  const [markupMenuOpen, setMarkupMenuOpen] = useState(false)
  const [hidden, setHidden] = useState<Set<SceneOverflowKey>>(() => new Set())
  const showHelp = !phoneLayout

  const present = useMemo(
    () => ({
      saveStart: canEdit && showSaveStart,
      goToStart: canEdit && showGoToStart,
      canEdit,
      qr: showFlyQr,
      viewQr: showViewQr,
      help: showHelp,
      trajectory: canEdit && !phoneLayout && Boolean(onToggleTrajectory),
      cameraPlace: canEdit && !phoneLayout && showCameraPlace && Boolean(onToggleCameraPlace),
      phoneFly: phoneLayout && canEdit && Boolean(onEnterPhoneFly),
    }),
    [
      canEdit,
      onEnterPhoneFly,
      onToggleCameraPlace,
      onToggleTrajectory,
      phoneLayout,
      showCameraPlace,
      showFlyQr,
      showViewQr,
      showGoToStart,
      showHelp,
      showSaveStart,
    ],
  )

  useLayoutEffect(() => {
    const root = toolbarRootRef.current
    const measure = measureRef.current
    if (!root || !measure || typeof ResizeObserver === "undefined") return

    const recompute = () => {
      const widths: SceneToolbarMeasuredWidths = {
        saveStart: measuredOuterWidth(
          measure.querySelector("[data-scene-measure='saveStart']"),
          FALLBACK_SCENE_TOOLBAR_WIDTHS.saveStart,
        ),
        goToStart: measuredOuterWidth(
          measure.querySelector("[data-scene-measure='goToStart']"),
          FALLBACK_SCENE_TOOLBAR_WIDTHS.goToStart,
        ),
        add: measuredOuterWidth(measure.querySelector("[data-scene-measure='add']"), FALLBACK_SCENE_TOOLBAR_WIDTHS.add),
        markup: measuredOuterWidth(
          measure.querySelector("[data-scene-measure='markup']"),
          FALLBACK_SCENE_TOOLBAR_WIDTHS.markup,
        ),
        undo: measuredOuterWidth(
          measure.querySelector("[data-scene-measure='undo']"),
          FALLBACK_SCENE_TOOLBAR_WIDTHS.undo,
        ),
        redo: measuredOuterWidth(
          measure.querySelector("[data-scene-measure='redo']"),
          FALLBACK_SCENE_TOOLBAR_WIDTHS.redo,
        ),
        history: measuredOuterWidth(
          measure.querySelector("[data-scene-measure='history']"),
          FALLBACK_SCENE_TOOLBAR_WIDTHS.history,
        ),
        trajectory: measuredOuterWidth(
          measure.querySelector("[data-scene-measure='trajectory']"),
          FALLBACK_SCENE_TOOLBAR_WIDTHS.trajectory,
        ),
        cameraPlace: measuredOuterWidth(
          measure.querySelector("[data-scene-measure='cameraPlace']"),
          FALLBACK_SCENE_TOOLBAR_WIDTHS.cameraPlace,
        ),
        light: measuredOuterWidth(
          measure.querySelector("[data-scene-measure='light']"),
          FALLBACK_SCENE_TOOLBAR_WIDTHS.light,
        ),
        fov: measuredOuterWidth(measure.querySelector("[data-scene-measure='fov']"), FALLBACK_SCENE_TOOLBAR_WIDTHS.fov),
        platform: measuredOuterWidth(
          measure.querySelector("[data-scene-measure='platform']"),
          FALLBACK_SCENE_TOOLBAR_WIDTHS.platform,
        ),
        speed: measuredOuterWidth(
          measure.querySelector("[data-scene-measure='speed']"),
          FALLBACK_SCENE_TOOLBAR_WIDTHS.speed,
        ),
        help: measuredOuterWidth(
          measure.querySelector("[data-scene-measure='help']"),
          FALLBACK_SCENE_TOOLBAR_WIDTHS.help,
        ),
        qr: measuredOuterWidth(measure.querySelector("[data-scene-measure='qr']"), FALLBACK_SCENE_TOOLBAR_WIDTHS.qr),
        viewQr: measuredOuterWidth(
          measure.querySelector("[data-scene-measure='viewQr']"),
          FALLBACK_SCENE_TOOLBAR_WIDTHS.viewQr,
        ),
        divider: measuredOuterWidth(
          measure.querySelector("[data-scene-measure='divider']"),
          FALLBACK_SCENE_TOOLBAR_WIDTHS.divider,
        ),
      }
      const nameMin = phoneLayout ? SCENE_NAME_MIN_PX_PHONE : SCENE_NAME_MIN_PX
      const available = Math.max(0, root.clientWidth - nameMin - widths.divider - TOOLBAR_PAD_X_PX)
      const next = computeSceneToolbarFit(available, present, widths)
      setHidden((prev) => (sceneOverflowSetsEqual(prev, next) ? prev : next))
    }

    recompute()
    const observer = new ResizeObserver(recompute)
    observer.observe(root)
    observer.observe(measure)
    return () => observer.disconnect()
  }, [phoneLayout, present])

  useEffect(() => {
    if (hidden.size === 0 && moreOpen) setMoreOpen(false)
  }, [hidden, moreOpen])

  const show = (key: SceneOverflowKey) => !hidden.has(key)

  const closeSiblingMenus = (except?: "history" | "light" | "platform" | "qr" | "viewQr" | "picker" | "markup") => {
    if (except !== "history") onHistoryOpenChange(false)
    if (except !== "light") onLightMenuOpenChange(false)
    if (except !== "platform") setPlatformMenuOpen(false)
    if (except !== "qr") setQrMenuOpen(false)
    if (except !== "viewQr") setViewQrMenuOpen(false)
    if (except !== "picker" && pickerOpen) (onDismissPicker ?? onTogglePicker)?.()
    if (except !== "markup") setMarkupMenuOpen(false)
  }

  const leaveCameraPlace = () => {
    onLeaveCameraPlace?.()
  }

  const moreItems: EditToolbarMoreItem[] = []
  if (canEdit && hidden.has("undo")) {
    moreItems.push({
      key: "undo",
      label: t("locationsPage.scene3d.undo"),
      icon: <Undo2 className="h-3.5 w-3.5" />,
      onClick: onUndo,
      disabled: !canUndo,
    })
  }
  if (canEdit && hidden.has("redo")) {
    moreItems.push({
      key: "redo",
      label: t("locationsPage.scene3d.redo"),
      icon: <Redo2 className="h-3.5 w-3.5" />,
      onClick: onRedo,
      disabled: !canRedo,
    })
  }
  if (canEdit && hidden.has("history")) {
    moreItems.push({
      key: "history",
      label: t("locationsPage.scene3d.history"),
      icon: <History className="h-3.5 w-3.5" />,
      onClick: () => {
        closeSiblingMenus("history")
        onHistoryOpenChange(true)
      },
    })
  }
  if (
    canEdit &&
    !phoneLayout &&
    onToggleCameraPlace &&
    showCameraPlace &&
    hidden.has("cameraPlace") &&
    !cameraPlaceActive
  ) {
    moreItems.push({
      key: "cameraPlace",
      label: t("locationsPage.scene3d.placeCamera"),
      icon: <CameraPlaceIcon className="h-3.5 w-3.5" />,
      disabled: cameraPlaceDisabled && !cameraPlaceActive,
      onClick: onToggleCameraPlace,
    })
  }
  if (canEdit && !phoneLayout && onToggleTrajectory && hidden.has("trajectory") && !trajectoryActive) {
    moreItems.push({
      key: "trajectory",
      label: t("locationsPage.scene3d.trajectory.mode"),
      icon: <CameraTrajectoryIcon className="h-3.5 w-3.5" />,
      onClick: () => {
        leaveCameraPlace()
        onToggleTrajectory()
      },
    })
  }
  if (canEdit && hidden.has("markup")) {
    moreItems.push({
      key: "markup",
      label: t("locationsPage.scene3d.environment.skyMarkup"),
      icon: <Grid2x2X className="h-3.5 w-3.5" />,
      onClick: () => {
        leaveCameraPlace()
        closeSiblingMenus("markup")
        setMarkupMenuOpen(true)
      },
    })
  }
  if (canEdit && hidden.has("light")) {
    moreItems.push({
      key: "light",
      label: t("locationsPage.scenePlatform.light"),
      icon: <SolarPanel className="h-3.5 w-3.5" />,
      onClick: () => {
        leaveCameraPlace()
        closeSiblingMenus("light")
        onLightMenuOpenChange(true)
      },
    })
  }
  if (canEdit && platformDraft && hidden.has("platform")) {
    moreItems.push({
      key: "platform",
      label: t("locationsPage.scenePlatform.modePlatform"),
      icon: <Scaling className="h-3.5 w-3.5" />,
      onClick: () => {
        leaveCameraPlace()
        closeSiblingMenus("platform")
        setPlatformMenuOpen(true)
      },
    })
  }
  if (showFlyQr && hidden.has("qr")) {
    moreItems.push({
      key: "qr",
      label: flyQrLabel,
      icon: <Drone className="h-3.5 w-3.5" />,
      onClick: () => {
        void (async () => {
          leaveCameraPlace()
          closeSiblingMenus("qr")
          if (onFlyQrBeforeOpen) {
            const ok = await onFlyQrBeforeOpen()
            if (!ok) return
          }
          setQrMenuOpen(true)
        })()
      },
    })
  }
  if (showViewQr && hidden.has("viewQr")) {
    moreItems.push({
      key: "viewQr",
      label: viewQrLabel,
      icon: <MonitorSmartphone className="h-3.5 w-3.5" />,
      onClick: () => {
        void (async () => {
          leaveCameraPlace()
          closeSiblingMenus("viewQr")
          if (onFlyQrBeforeOpen) {
            const ok = await onFlyQrBeforeOpen()
            if (!ok) return
          }
          setViewQrMenuOpen(true)
        })()
      },
    })
  }
  const moreHeader = hidden.has("fov") ? (
    <div className="flex flex-col gap-2">
      {hidden.has("fov") ? (
        <ToolbarSliderButton
          icon={Aperture}
          label={t("locationsPage.scene3d.fov")}
          valueText={`${Math.round(fov)}°`}
          min={25}
          max={100}
          step={1}
          value={fov}
          onValueChange={onFovChange}
          onOpen={leaveCameraPlace}
          control={<SceneFovMenuControl value={fov} onValueChange={onFovChange} />}
          menuWidthPx={FOV_MENU_WIDTH_PX}
          menuHeightPx={FOV_MENU_HEIGHT_PX}
          menuAlign="center"
        />
      ) : null}
    </div>
  ) : null

  const showMore = moreItems.length > 0 || Boolean(moreHeader)

  const lightSides = [
    ["north", "locationsPage.scenePlatform.lightNorth"],
    ["east", "locationsPage.scenePlatform.lightEast"],
    ["south", "locationsPage.scenePlatform.lightSouth"],
    ["west", "locationsPage.scenePlatform.lightWest"],
    ["top", "locationsPage.scenePlatform.lightTop"],
  ] as const

  const historyButton = (
    <SceneToolbarMenu
      open={historyOpen}
      onOpenChange={onHistoryOpenChange}
      onBeforeOpen={() => closeSiblingMenus("history")}
      title={t("locationsPage.scene3d.history")}
      icon={<History className="h-4 w-4" />}
      estimatedMenuWidthPx={224}
      estimatedMenuHeightPx={280}
      panelClassName="flex w-56 flex-col overflow-hidden"
    >
      <div
        data-scene-wheel-scroll
        onWheel={(event) => {
          event.stopPropagation()
        }}
      >
        <div className="flex shrink-0 items-center justify-between gap-2 border-b border-ui-border px-2 py-1.5">
          <p className="text-[11px] font-medium text-ui-text-primary">{t("locationsPage.scene3d.history")}</p>
          <button
            type="button"
            className="text-[10px] text-ui-text-muted hover:text-ui-text-primary"
            onClick={onClearHistory}
          >
            {t("locationsPage.scene3d.historyClear")}
          </button>
        </div>
        <div className="it2tv-scrollbar max-h-52 overflow-y-auto overscroll-contain py-1" data-scene-wheel-scroll>
          {historyEntries.length === 0 ? (
            <p className="px-2 py-2 text-[11px] text-ui-text-muted">{t("locationsPage.scene3d.historyEmpty")}</p>
          ) : (
            historyEntries.map((entry) => (
              <button
                key={entry.id}
                type="button"
                className="flex w-full flex-col items-start gap-0.5 px-2 py-1.5 text-left text-[11px] text-ui-text-primary hover:bg-ui-surface-sunken"
                onClick={() => onRestoreHistory(entry)}
              >
                <span>{t(entry.labelKey as "locationsPage.scene3d.history", entry.labelParams)}</span>
                <span className="text-[10px] text-ui-text-muted">{new Date(entry.at).toLocaleString()}</span>
              </button>
            ))
          )}
        </div>
      </div>
    </SceneToolbarMenu>
  )

  const lightButton = (
    <SceneToolbarMenu
      open={lightMenuOpen}
      onOpenChange={onLightMenuOpenChange}
      onBeforeOpen={() => {
        leaveCameraPlace()
        closeSiblingMenus("light")
      }}
      title={t("locationsPage.scenePlatform.light")}
      icon={<SolarPanel className="h-4 w-4" />}
      estimatedMenuWidthPx={176}
      estimatedMenuHeightPx={200}
      panelClassName="flex w-44 flex-col overflow-hidden py-1"
    >
      {lightSides.map(([side, labelKey]) => (
        <button
          key={side}
          type="button"
          className={cn(
            "px-2.5 py-1.5 text-left text-[11px] text-ui-text-primary hover:bg-ui-surface-sunken",
            lightPreset === side && "bg-ui-accent-soft text-ui-accent-text",
          )}
          onClick={() => {
            onApplyLightPreset(side)
            onLightMenuOpenChange(false)
          }}
        >
          {t(labelKey)}
        </button>
      ))}
    </SceneToolbarMenu>
  )

  const markupButton = (
    <SceneToolbarMenu
      open={markupMenuOpen}
      onOpenChange={setMarkupMenuOpen}
      onBeforeOpen={() => {
        leaveCameraPlace()
        closeSiblingMenus("markup")
      }}
      title={t("locationsPage.scene3d.environment.skyMarkup")}
      icon={<Grid2x2X className="h-4 w-4" />}
      estimatedMenuWidthPx={176}
      estimatedMenuHeightPx={108}
      panelClassName="flex w-44 flex-col overflow-hidden py-1"
    >
      {(
        [
          ["plus", "locationsPage.scene3d.environment.markupPlus", Plus, "h-3.5 w-3.5"],
          ["dots", "locationsPage.scene3d.environment.markupDots", Circle, "h-1.5 w-1.5 fill-current"],
          ["none", "locationsPage.scene3d.environment.markupNone", Minus, "h-3.5 w-3.5"],
        ] as const
      ).map(([id, labelKey, Icon, iconClass]) => (
        <button
          key={id}
          type="button"
          className={cn(
            "flex items-center gap-1.5 px-2.5 py-1.5 text-left text-[11px] text-ui-text-primary hover:bg-ui-surface-sunken",
            skyMarkup === id && "bg-ui-accent-soft text-ui-accent-text",
          )}
          onClick={() => {
            onSkyMarkupChange(id)
            setMarkupMenuOpen(false)
          }}
        >
          <span className="flex h-4 w-4 shrink-0 items-center justify-center">
            <Icon className={iconClass} aria-hidden />
          </span>
          {t(labelKey)}
        </button>
      ))}
    </SceneToolbarMenu>
  )

  const platformButton =
    canEdit && platformDraft ? (
      <SceneToolbarMenu
        open={platformMenuOpen}
        onOpenChange={setPlatformMenuOpen}
        onBeforeOpen={() => {
          leaveCameraPlace()
          closeSiblingMenus("platform")
        }}
        title={t("locationsPage.scenePlatform.modePlatform")}
        icon={<Scaling className="h-4 w-4" />}
        estimatedMenuWidthPx={176}
        estimatedMenuHeightPx={260}
        panelClassName="p-3"
      >
        <PlatformDimsControls
          draft={platformDraft}
          canEdit={canEdit}
          busy={platformBusy}
          onChange={onPlatformDraftChange}
          onApply={onPlatformApply}
        />
      </SceneToolbarMenu>
    ) : null

  const qrButton = showFlyQr ? (
    <SceneToolbarMenu
      open={qrMenuOpen}
      onOpenChange={setQrMenuOpen}
      disabled={flyQrBusy}
      onBeforeOpen={async () => {
        leaveCameraPlace()
        closeSiblingMenus("qr")
        if (onFlyQrBeforeOpen) {
          const ok = await onFlyQrBeforeOpen()
          if (!ok) return false
        }
      }}
      title={flyQrLabel}
      icon={<Drone className="h-4 w-4" />}
      align="right"
      estimatedMenuWidthPx={120}
      estimatedMenuHeightPx={140}
      panelClassName="p-3"
    >
      <Scene3dFlyQr url={flyUrl} label={flyQrLabel} />
    </SceneToolbarMenu>
  ) : null

  const viewQrButton = showViewQr ? (
    <SceneToolbarMenu
      open={viewQrMenuOpen}
      onOpenChange={setViewQrMenuOpen}
      disabled={flyQrBusy}
      onBeforeOpen={async () => {
        leaveCameraPlace()
        closeSiblingMenus("viewQr")
        if (onFlyQrBeforeOpen) {
          const ok = await onFlyQrBeforeOpen()
          if (!ok) return false
        }
      }}
      title={viewQrLabel}
      icon={<MonitorSmartphone className="h-4 w-4" />}
      align="right"
      estimatedMenuWidthPx={120}
      estimatedMenuHeightPx={140}
      panelClassName="p-3"
    >
      <Scene3dFlyQr url={viewUrl} label={viewQrLabel} />
    </SceneToolbarMenu>
  ) : null

  const labeledBtn = (
    icon: ReactNode,
    label: string,
    opts: {
      onClick: () => void
      disabled?: boolean
      busy?: boolean
    },
  ) => (
    <EditToolbarButton title={label} hint="tooltip" disabled={opts.disabled} onClick={opts.onClick}>
      {opts.busy ? <Loader2 className="h-4 w-4 animate-spin" /> : icon}
    </EditToolbarButton>
  )

  return (
    <div ref={toolbarRootRef} className={TOOLBAR_ROW}>
      <div
        ref={measureRef}
        className="pointer-events-none fixed top-0 left-0 -z-50 flex items-center gap-1.5 opacity-0"
        aria-hidden
      >
        <span data-scene-measure="saveStart">
          <EditToolbarButton title="m" onClick={() => {}}>
            <MapPin className="h-4 w-4" />
          </EditToolbarButton>
        </span>
        <span data-scene-measure="goToStart">
          <EditToolbarButton title="m" onClick={() => {}}>
            <LocateFixed className="h-4 w-4" />
          </EditToolbarButton>
        </span>
        <span data-scene-measure="add">
          <EditToolbarButton title="m" onClick={() => {}}>
            <Plus className="h-4 w-4" />
          </EditToolbarButton>
        </span>
        <span data-scene-measure="markup">
          <EditToolbarButton title="m" onClick={() => {}}>
            <Grid2x2X className="h-4 w-4" />
          </EditToolbarButton>
        </span>
        <span data-scene-measure="undo">
          <EditToolbarButton title="m" onClick={() => {}}>
            <Undo2 className="h-4 w-4" />
          </EditToolbarButton>
        </span>
        <span data-scene-measure="redo">
          <EditToolbarButton title="m" onClick={() => {}}>
            <Redo2 className="h-4 w-4" />
          </EditToolbarButton>
        </span>
        <span data-scene-measure="history">
          <EditToolbarButton title="m" onClick={() => {}}>
            <History className="h-4 w-4" />
          </EditToolbarButton>
        </span>
        <span data-scene-measure="trajectory">
          <EditToolbarButton title="m" onClick={() => {}}>
            <CameraTrajectoryIcon className="h-4 w-4" />
          </EditToolbarButton>
        </span>
        <span data-scene-measure="cameraPlace">
          <EditToolbarButton title="m" onClick={() => {}}>
            <CameraPlaceIcon className="h-4 w-4" />
          </EditToolbarButton>
        </span>
        <span data-scene-measure="light">
          <EditToolbarButton title="m" onClick={() => {}}>
            <SolarPanel className="h-4 w-4" />
          </EditToolbarButton>
        </span>
        <span data-scene-measure="fov">
          <ToolbarSliderButton
            icon={Aperture}
            label={t("locationsPage.scene3d.fov")}
            valueText={`${Math.round(fov)}°`}
            min={25}
            max={100}
            step={1}
            value={fov}
            onValueChange={onFovChange}
            control={<SceneFovMenuControl value={fov} onValueChange={onFovChange} />}
            menuWidthPx={FOV_MENU_WIDTH_PX}
            menuHeightPx={FOV_MENU_HEIGHT_PX}
            menuAlign="center"
          />
        </span>
        <span data-scene-measure="platform">
          <EditToolbarButton title="m" onClick={() => {}}>
            <Scaling className="h-4 w-4" />
          </EditToolbarButton>
        </span>
        <span data-scene-measure="speed">
          <ToolbarSliderButton
            icon={Gauge}
            label={t("locationsPage.scene3d.speed")}
            valueInPanel
            min={1}
            max={12}
            step={0.5}
            value={flySpeed}
            onValueChange={onFlySpeedChange}
          />
        </span>
        <span data-scene-measure="help">
          <EditToolbarButton title="m" onClick={() => {}}>
            <HelpCircle className="h-4 w-4" />
          </EditToolbarButton>
        </span>
        <span data-scene-measure="qr">
          <EditToolbarButton title="m" onClick={() => {}}>
            <Drone className="h-4 w-4" />
          </EditToolbarButton>
        </span>
        <span data-scene-measure="viewQr">
          <EditToolbarButton title="m" onClick={() => {}}>
            <MonitorSmartphone className="h-4 w-4" />
          </EditToolbarButton>
        </span>
        <span data-scene-measure="divider" className="mx-0.5 h-5 w-px bg-ui-border" />
      </div>

      <div className="min-w-0 flex-1">
        <SceneToolbarName name={sceneName} canEdit={canEdit} onCommit={onSceneNameCommit} />
      </div>
      {present.saveStart || present.goToStart ? DIVIDER : null}
      <div className="flex shrink-0 flex-nowrap items-center gap-1.5">
        {present.saveStart
          ? labeledBtn(<MapPin className="h-4 w-4" />, t("locationsPage.scene3d.saveStart"), {
              onClick: () => {
                leaveCameraPlace()
                onSaveStart()
              },
            })
          : null}
        {present.goToStart
          ? labeledBtn(<LocateFixed className="h-4 w-4" />, t("locationsPage.scene3d.goToStart"), {
              onClick: () => {
                leaveCameraPlace()
                onGoToStart()
              },
              busy: goToStartBusy,
              disabled: goToStartBusy || !canGoToStart,
            })
          : null}
        {canEdit && show("undo") ? (
          <EditToolbarButton
            title={t("locationsPage.scene3d.undo")}
            hint="tooltip"
            disabled={!canUndo}
            onClick={onUndo}
          >
            <Undo2 className="h-4 w-4" />
          </EditToolbarButton>
        ) : null}
        {canEdit && show("redo") ? (
          <EditToolbarButton
            title={t("locationsPage.scene3d.redo")}
            hint="tooltip"
            disabled={!canRedo}
            onClick={onRedo}
          >
            <Redo2 className="h-4 w-4" />
          </EditToolbarButton>
        ) : null}
        {canEdit && (show("history") || historyOpen) ? historyButton : null}
        {(show("trajectory") ||
          trajectoryActive ||
          show("cameraPlace") ||
          cameraPlaceActive ||
          show("markup") ||
          show("light") ||
          show("fov") ||
          show("platform") ||
          show("qr") ||
          show("viewQr") ||
          markupMenuOpen ||
          lightMenuOpen ||
          platformMenuOpen ||
          qrMenuOpen ||
          viewQrMenuOpen) &&
        canEdit
          ? DIVIDER
          : null}

        {canEdit && onTogglePicker ? (
          <EditToolbarButton
            ref={addButtonRef}
            title={t("locationsPage.scene3d.add")}
            hint="tooltip"
            active={pickerToolbarActive ?? pickerOpen}
            onClick={() => {
              leaveCameraPlace()
              closeSiblingMenus("picker")
              onTogglePicker()
            }}
          >
            <Plus className="h-4 w-4" />
          </EditToolbarButton>
        ) : null}
        {phoneLayout && canEdit && onEnterPhoneFly ? (
          <EditToolbarButton
            title={flyQrLabel}
            hint="tooltip"
            onClick={() => {
              leaveCameraPlace()
              closeSiblingMenus()
              onEnterPhoneFly()
            }}
          >
            <Drone className="h-4 w-4" />
          </EditToolbarButton>
        ) : null}
        {canEdit &&
        !phoneLayout &&
        showCameraPlace &&
        onToggleCameraPlace &&
        (show("cameraPlace") || cameraPlaceActive) ? (
          <ActionHintTooltip
            label={cameraPlaceHint}
            open={cameraPlaceHint ? true : undefined}
            side="bottom"
            delayDuration={0}
            className="h-auto"
            contentClassName="z-[200]"
          >
            <EditToolbarButton
              title={t("locationsPage.scene3d.placeCamera")}
              hint={cameraPlaceHint ? undefined : "tooltip"}
              active={cameraPlaceActive}
              disabled={cameraPlaceDisabled && !cameraPlaceActive}
              onClick={() => {
                closeSiblingMenus()
                onToggleCameraPlace()
              }}
            >
              <CameraPlaceIcon className="h-4 w-4" />
            </EditToolbarButton>
          </ActionHintTooltip>
        ) : null}
        {canEdit && !phoneLayout && onToggleTrajectory && (show("trajectory") || trajectoryActive) ? (
          <TrajectoryTool
            active={trajectoryActive}
            title={t("locationsPage.scene3d.trajectory.mode")}
            panel={trajectoryPanel}
            onToggle={() => {
              leaveCameraPlace()
              closeSiblingMenus()
              onToggleTrajectory()
            }}
          />
        ) : null}
        {canEdit && (show("markup") || markupMenuOpen) ? markupButton : null}
        {canEdit && (show("light") || lightMenuOpen) ? lightButton : null}
        {canEdit && show("fov") ? (
          <ToolbarSliderButton
            icon={Aperture}
            label={t("locationsPage.scene3d.fov")}
            valueText={`${Math.round(fov)}°`}
            min={25}
            max={100}
            step={1}
            value={fov}
            onValueChange={onFovChange}
            onOpen={leaveCameraPlace}
            control={<SceneFovMenuControl value={fov} onValueChange={onFovChange} />}
            menuWidthPx={FOV_MENU_WIDTH_PX}
            menuHeightPx={FOV_MENU_HEIGHT_PX}
            menuAlign="center"
          />
        ) : null}
        {canEdit && platformDraft && (show("platform") || platformMenuOpen) ? platformButton : null}

        {show("qr") || qrMenuOpen ? qrButton : null}
        {show("viewQr") || viewQrMenuOpen ? viewQrButton : null}

        {showMore ? (
          <EditToolbarMore
            open={moreOpen}
            onOpenChange={(open) => {
              if (open) leaveCameraPlace()
              setMoreOpen(open)
            }}
            active={moreOpen}
            menuAria={t("locationsPage.scene3d.toolbarMore")}
            hint="tooltip"
            moreButtonRef={moreButtonRef}
            items={moreItems}
            header={moreHeader ?? undefined}
            menuClassName="scene-toolbar"
            portalToBody
          />
        ) : null}

        {showHelp && show("help") ? (
          <ActionHintTooltip content={<LocationScene3dControlsHint />} delayDuration={200} contentClassName="p-2.5">
            <span
              aria-label={t("locationsPage.scene3d.controlsHelp")}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md text-ui-text-secondary"
            >
              <HelpCircle className="h-4 w-4" />
            </span>
          </ActionHintTooltip>
        ) : null}

        {phoneLayout && onRequestClose ? (
          <EditToolbarButton
            title={t("locationsPage.scene3d.close")}
            hint="tooltip"
            onClick={() => {
              leaveCameraPlace()
              onRequestClose()
            }}
            className="ml-auto"
          >
            <X className="h-4 w-4" />
          </EditToolbarButton>
        ) : null}
      </div>
    </div>
  )
}
